import { ChangeEvent, useMemo, useState } from 'react';
import { searchStores } from '@api/interfaces/storeApi';
import debounce from '@helpers/debounce';
import Config from '@config';
import { type SuggestionType } from '@molecules/PickupLocationListItem/PickupLocationListItem';
import { trackStoreSuggestionClick, trackStoreSuggestionsEmpty } from '@helpers/analyticsHelpers/trackStoreFinder';
import logger from '@logger';

const MIN_SEARCH_LENGTH = 2;

interface UseStoreSearchOptions {
  onSearchComplete?: (results: SuggestionType[]) => void;
}

const useStoreSearch = ({ onSearchComplete }: UseStoreSearchOptions = {}) => {
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState<SuggestionType[]>([]);
  const [shouldShowSuggestions, setShouldShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const search = async (value: string) => {
    try {
      const res = await searchStores(value.toLowerCase(), 'score-desc');
      setSuggestions(res?.results ?? []);
      setShouldShowSuggestions(true);
      onSearchComplete?.(res?.results ?? []);
      if (!res.results) {
        trackStoreSuggestionsEmpty();
      }
    } catch (e) {
      logger.error({ error: 'Could not fetch stores.', message: e instanceof Error ? e.message : String(e) });
    } finally {
      setIsLoading(false);
    }
  };

  const debouncedSearch = useMemo(() => debounce(search, Config.TIMEOUT.storeFinderGetPickupLocationSuggestionsMs), []);

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget;
    setInputValue(value);

    if (value.length >= MIN_SEARCH_LENGTH) {
      setIsLoading(true);
      debouncedSearch(value);
    } else {
      debouncedSearch.cancel();
      setIsLoading(false);
      setShouldShowSuggestions(false);
    }
  };

  const onClear = () => {
    setInputValue('');
    setSuggestions([]);
    setShouldShowSuggestions(false);
    setIsLoading(false);
    debouncedSearch.cancel();
  };

  const selectStore = (store: SuggestionType) => {
    setShouldShowSuggestions(false);
    setInputValue(store.displayName || store.name || '');
    trackStoreSuggestionClick(store);
  };

  return {
    inputValue,
    setInputValue,
    suggestions,
    shouldShowSuggestions,
    setShouldShowSuggestions,
    onInputChange,
    onClear,
    selectStore,
    isLoading,
  };
};

export default useStoreSearch;
